import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/auth';
import { BrandMark } from '../components/BrandMark';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';

/**
 * Where to send the user after a successful login.
 *
 * Only same-origin, absolute-path routes are honoured. Sending an arbitrary
 * `?next=` value to navigate() would be an open redirect: `?next=//evil.example`
 * is protocol-relative and leaves the site, and `?next=https://evil.example`
 * leaves it outright — which turns our own login page into a credible-looking
 * springboard for a phishing link.
 *
 * So: must start with a single "/", must not start with "//", and must contain
 * no scheme. Anything else falls back to the dashboard.
 */
function safeNext(raw: string | null): string {
  if (!raw) return '/dashboard';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/dashboard';
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return '/dashboard';
  return raw;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // /pricing links returning customers to /login?next=/billing so the "Upgrade
  // in Billing" path actually lands on billing instead of the dashboard.
  const next = safeNext(searchParams.get('next'));
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setNeedsVerify(false);
    setResendState('idle');

    try {
      await login({ email, password });
      navigate(next);
    } catch (err: any) {
      // The account exists and the password is correct, but the email isn't
      // verified — offer a resend instead of a dead-end error.
      if (err?.response?.data?.error?.details?.code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerify(true);
        clearError();
      }
      console.error('Login failed:', err);
    }
  };

  const handleResend = async () => {
    setResendState('sending');
    try {
      await authService.sendVerificationEmail(email);
    } finally {
      // The endpoint always returns success (doesn't reveal if the email exists).
      setResendState('sent');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-2">
            <BrandMark className="h-12 w-12 text-slate-900" />
          </div>
          <CardTitle className="text-2xl text-center">Welcome to AgentMesh</CardTitle>
          <CardDescription className="text-center">
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && !needsVerify && (
              <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}
            {needsVerify && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
                <p className="font-medium">Please verify your email first</p>
                <p>
                  We sent a verification link to <strong>{email}</strong>. Click it to
                  activate your account, then sign in. Check your spam folder too.
                </p>
                {resendState === 'sent' ? (
                  <p className="font-medium text-green-700">
                    ✓ Verification email re-sent — check your inbox.
                  </p>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResend}
                    disabled={resendState === 'sending'}
                  >
                    {resendState === 'sending'
                      ? 'Sending…'
                      : 'Resend verification email'}
                  </Button>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline font-medium">
                Sign up
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
